import openSocket from "socket.io-client";
import { REACT_APP_BACKEND_URL } from "../config";

class SocketManager {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.wasDisconnected = false;
    this.disconnectedAt = null;

    // Listener Maps
    this.componentListeners = new Map(); // event -> [{ componentId, callback }]
    this.globalListeners = new Map();    // event -> callback

    // Internal
    this.rooms = new Set();             // Rooms activas (chat web sockets)
    this.ticketRooms = new Set();       // Tickets en los que estamos joined
  }

  //  Conexión
  getConnection(companyId) {
    if (this.socket && this.isConnected) return this.socket;

    if (this.socket) {
      // Reusar instancia
      this.socket.connect();
      return this.socket;
    }

    return this.connect(companyId);
  }

  connect(companyId) {
    const userId = localStorage.getItem("userId");
    const profile = localStorage.getItem("profile");
    // const userQueues = localStorage.getItem("userQueues"); // Alternativa
    if (!userId) return // Si no hay este dato no debería conectarse

    this.socket = openSocket(REACT_APP_BACKEND_URL, {
      transports: ["websocket", "polling"],
      query: { userId, companyId, profile },
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 20000,
      timeout: 20000,
      forceNew: true
    });

    this._attachCoreHandlers();
    return this.socket;
  }

  // Handlers básicos del socket
  _attachCoreHandlers() {
    this.socket.on("connect", () => {
      const isReconnect = this.wasDisconnected;

      this.isConnected = true;

      console.info(`🔌 Socket conectado: ${this.socket.id}`);

      this._rebindComponentListeners();
      this._rejoinRooms();

      // Si NO venimos de desconexión real
      if (!isReconnect) return;

      // Tiempo desconectado
      const disconnectedTime = Date.now() - this.disconnectedAt;

      console.log("⏱️ Tiempo desconectado:", disconnectedTime);

      // Reset flags
      this.wasDisconnected = false;
      this.disconnectedAt = null;

      // Ignorar micro-desconexiones
      if (disconnectedTime < 5000) {
        console.log("🟡 Reconexión rápida ignorada");
        return;
      }

      console.warn("♻️ Reconexión real detectada, re-suscribiendo rooms...");
    });

    this.socket.on("disconnect", reason => {
      this.isConnected = false;
      this.wasDisconnected = true;
      this.disconnectedAt = Date.now();
    
      console.warn("⚠️ Socket desconectado:", reason);
    });

    this.socket.on("connect_error", error => {
      console.error("❌ Error de conexión:", error.message);
    });

    this.socket.on("reconnect", attempt => {
      console.log(`🔄 Reconectado en intento ${attempt}`);
    });
  }

  // Rooms
  joinRoom(room, payload) {
    if (!this.isConnected) return;
    this.socket.emit(room, payload);
    this.rooms.add(room);
  }

  leaveRoom(room, payload) {
    if (!this.isConnected) return;
    this.socket.emit(room, payload);
    this.rooms.delete(room);
  }

  // 🆕 Room de ticket: persistencia para evitar joins duplicados por re-mounts.
  // Idempotente: si ya estamos joined al ticket, no emite nada.
  joinTicketRoom(ticketId, companyId) {
    const key = `ticket:${ticketId}`;
    if (this.ticketRooms.has(key)) {
      console.log(`📡 [SocketManager] ticket:${ticketId} ya en rooms, skip JOIN`);
      return;
    }
    this.ticketRooms.add(key);
    if (this.isConnected) {
      console.log(`📡 [SocketManager] JOIN ticket=${ticketId} company=${companyId}`);
      this.socket.emit("join:ticket", { ticketId, companyId });
    }
  }

  leaveTicketRoom(ticketId) {
    const key = `ticket:${ticketId}`;
    if (!this.ticketRooms.has(key)) return;
    this.ticketRooms.delete(key);
    if (this.isConnected) {
      console.log(`📡 [SocketManager] LEAVE ticket=${ticketId}`);
      this.socket.emit("leave:ticket", { ticketId });
    }
  }

  _rejoinRooms() {
    this.rooms.forEach((room) => {
      this.socket.emit("join", room);
    });
    // Re-join de tickets tras reconexión
    if (this.ticketRooms.size > 0) {
      const companyId = localStorage.getItem("companyId");
      this.ticketRooms.forEach((key) => {
        const ticketId = key.split(":")[1];
        console.log(`📡 [SocketManager] re-JOIN ticket=${ticketId}`);
        this.socket.emit("join:ticket", { ticketId, companyId });
      });
    }
  }

  // Listeners
  // Global listener (1 por evento)
  registerGlobal(event, callback) {
    if (this.globalListeners.has(event)) {
      console.warn(`Global listener ya existe para evento: ${event}`);
      return;
    }

    this.globalListeners.set(event, callback);
    this.socket?.on(event, callback);
  }

  unregisterGlobal(event) {
    const cb = this.globalListeners.get(event);
    if (cb) {
      this.socket?.off(event, cb);
      this.globalListeners.delete(event);
    }
  }

  _rebindComponentListeners() {
    if (!this.socket) return;
    for (const [event, handlers] of this.componentListeners.entries()) {
      handlers.forEach(({ callback }) => {
        this.socket.off(event, callback);
        this.socket.on(event, callback);
      });
    }
  }

  // Component listeners
  on(event, callback, componentId) {
    if (!componentId) {
      console.error("Component listener requiere componentId");
      return;
    }

    if (!this.socket) {
      const companyId = localStorage.getItem("companyId");
      this.getConnection(companyId);
    }

    if (!this.componentListeners.has(event)) {
      this.componentListeners.set(event, []);
    }

    this.componentListeners.get(event).push({ componentId, callback });
    this.socket?.on(event, callback);
  }

  cleanupComponent(componentId) {
    for (const [event, handlers] of this.componentListeners.entries()) {
      handlers.forEach(({ componentId: id, callback }) => {
        if (id === componentId) {
          this.socket?.off(event, callback);
        }
      });

      // limpiar lista
      this.componentListeners.set(
        event,
        handlers.filter(h => h.componentId !== componentId)
      );
    }
  }

  // Emit
  emit(event, payload) {
    if (!this.isConnected) return;
    this.socket.emit(event, payload);
  }

  // Disconnect
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.rooms.clear();
      this.componentListeners.clear();
      this.globalListeners.clear();
    }
  }

  // Debug Stats
  stats() {
    return {
      connected: this.isConnected,
      socketId: this.socket?.id,
      rooms: Array.from(this.rooms),
      globalListeners: Array.from(this.globalListeners.keys()),
      componentEvents: Array.from(this.componentListeners.entries()),
    };
  }
}

const socketManager = new SocketManager();

// Exports convenientes
export const getSocket = (companyId) => socketManager.getConnection(companyId);
export const joinRoom = (room, payload) => socketManager.joinRoom(room, payload);
export const leaveRoom = (room, payload) => socketManager.leaveRoom(room, payload);
export const joinTicketRoom = (ticketId, companyId) => socketManager.joinTicketRoom(ticketId, companyId);
export const leaveTicketRoom = (ticketId) => socketManager.leaveTicketRoom(ticketId);
export const emitSocket = (event, data) => socketManager.emit(event, data);
export const onSocket = (event, cb, componentId) =>
  socketManager.on(event, cb, componentId);
export const registerGlobal = (event, cb) =>
  socketManager.registerGlobal(event, cb);
export const unregisterGlobal = (event) =>
  socketManager.unregisterGlobal(event);
export const cleanupComponent = (id) =>
  socketManager.cleanupComponent(id);
export const socketStats = () => socketManager.stats();
export const disconnectSocket = () => socketManager.disconnect();

export default socketManager;
